import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";
import type {
  AccountKind,
  AppNotification,
  Appointment,
  ChatMessage,
  ClientConnectionStatus,
  ClientRecord,
  Conversation,
  ConversationPresence,
  Employee,
  Invoice,
  MessageAttachment,
  Organization,
  OrganizationMember,
  WorkspaceSubscription,
} from "../lib/types";
import { useAuth } from "./AuthContext";
import { activateDuePlannedAppointments } from "../lib/workflow";
import { useI18n } from "../lib/i18n";

interface WorkspaceValue {
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  accountKind: AccountKind;
  organizationId: string | null;
  activeClientId: string | null;
  organization: Organization | null;
  membership: OrganizationMember | null;
  isOwner: boolean;
  isEmployee: boolean;
  currentEmployee: Employee | null;
  subscription: WorkspaceSubscription | null;
  clients: ClientRecord[];
  archivedClients: ClientRecord[];
  appointments: Appointment[];
  employees: Employee[];
  conversations: Conversation[];
  messages: ChatMessage[];
  attachments: MessageAttachment[];
  invoices: Invoice[];
  notifications: AppNotification[];
  clientConnectionStatuses: Record<string, ClientConnectionStatus>;
  conversationPresence: Record<string, ConversationPresence>;
  refresh: (silent?: boolean) => Promise<void>;
  clientFor: (id: string) => ClientRecord | undefined;
  conversationFor: (clientId: string) => Conversation | undefined;
  connectionStatusFor: (clientId: string) => ClientConnectionStatus;
}

const WorkspaceContext = createContext<WorkspaceValue | null>(null);

interface AuthenticatedAccountContext {
  profile_id: string;
  account_kind: AccountKind | null;
  organization_id: string | null;
  organization_role: OrganizationMember["role"] | null;
  linked_client_id: string | null;
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth();
  const { t } = useI18n();
  const accountKind = profile?.default_account_kind ?? "business";
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [activeClientId, setActiveClientId] = useState<string | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [membership, setMembership] = useState<OrganizationMember | null>(null);
  const [subscription, setSubscription] =
    useState<WorkspaceSubscription | null>(null);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [archivedClients, setArchivedClients] = useState<ClientRecord[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [clientConnectionStatuses, setClientConnectionStatuses] = useState<
    Record<string, ClientConnectionStatus>
  >({});
  const [conversationPresence, setConversationPresence] = useState<
    Record<string, ConversationPresence>
  >({});
  const realtimeRefreshTimer = useRef<number | null>(null);
  const isEmployeeAccount =
    accountKind === "business" &&
    Boolean(membership) &&
    membership?.role !== "owner";

  const clear = useCallback(() => {
    setOrganizationId(null);
    setActiveClientId(null);
    setOrganization(null);
    setMembership(null);
    setSubscription(null);
    setClients([]);
    setArchivedClients([]);
    setAppointments([]);
    setEmployees([]);
    setConversations([]);
    setMessages([]);
    setAttachments([]);
    setInvoices([]);
    setNotifications([]);
    setClientConnectionStatuses({});
    setConversationPresence({});
  }, []);

  const refresh = useCallback(
    async (silent = false) => {
      if (
        !user ||
        !profile?.onboarding_completed_at ||
        !profile.default_account_kind
      ) {
        clear();
        setLoading(false);
        return;
      }
      if (!silent) setRefreshing(true);
      setError(null);
      try {
        let resolvedOrganizationId: string | null = null;
        let resolvedClientId: string | null = null;
        let resolvedMembership: OrganizationMember | null = null;

        const { data: contextData, error: contextError } = await supabase.rpc(
          "get_authenticated_account_context",
        );
        if (contextError) throw contextError;
        const accountContext =
          ((contextData as AuthenticatedAccountContext[] | null) ?? [])[0] ??
          null;
        if (
          !accountContext ||
          accountContext.profile_id !== user.id ||
          accountContext.account_kind !== profile.default_account_kind
        ) {
          throw new Error(
            "Your existing workspace connection could not be verified.",
          );
        }
        resolvedOrganizationId = accountContext.organization_id;
        resolvedClientId = accountContext.linked_client_id;
        if (
          profile.default_account_kind === "business" &&
          accountContext.organization_id &&
          accountContext.organization_role
        ) {
          resolvedMembership = {
            organization_id: accountContext.organization_id,
            profile_id: user.id,
            role: accountContext.organization_role,
            status: "active",
          };
        }

        const resolvedIsOwner =
          profile.default_account_kind === "business" &&
          resolvedMembership?.role === "owner";

        setOrganizationId(resolvedOrganizationId);
        setActiveClientId(resolvedClientId);
        setMembership(resolvedMembership);
        if (!resolvedOrganizationId) {
          setOrganization(null);
          setClients([]);
          setArchivedClients([]);
          setAppointments([]);
          setEmployees([]);
          setConversations([]);
          setMessages([]);
          setAttachments([]);
          setInvoices([]);
          setNotifications([]);
          setClientConnectionStatuses({});
          setConversationPresence({});
          return;
        }

        if (resolvedIsOwner) {
          await activateDuePlannedAppointments(resolvedOrganizationId);
        }

        const canViewBusinessFinance = resolvedIsOwner;
        const canViewCommunications =
          profile.default_account_kind === "client" || resolvedIsOwner;

        const [
          organizationResult,
          clientsResult,
          appointmentsResult,
          employeesResult,
          conversationsResult,
          messagesResult,
          attachmentsResult,
          invoicesResult,
          notificationsResult,
          subscriptionResult,
          connectionStatusesResult,
          presenceResult,
        ] = await Promise.all([
          supabase.rpc("get_workspace_organization", {
            p_organization_id: resolvedOrganizationId,
          }),
          supabase.rpc("list_workspace_clients", {
            p_organization_id: resolvedOrganizationId,
          }),
          supabase.rpc("list_workspace_appointments", {
            p_organization_id: resolvedOrganizationId,
          }),
          profile.default_account_kind === "business"
            ? supabase
                .from("employees")
                .select("*")
                .eq("organization_id", resolvedOrganizationId)
                .order("display_name")
            : Promise.resolve({ data: [], error: null }),
          canViewCommunications
            ? supabase
                .from("conversations")
                .select("*")
                .eq("organization_id", resolvedOrganizationId)
                .order("updated_at", { ascending: false })
            : Promise.resolve({ data: [], error: null }),
          canViewCommunications
            ? supabase
                .from("messages")
                .select("*")
                .eq("organization_id", resolvedOrganizationId)
                .order("sent_at")
            : Promise.resolve({ data: [], error: null }),
          canViewCommunications
            ? supabase
                .from("message_attachments")
                .select("*")
                .eq("organization_id", resolvedOrganizationId)
                .order("created_at")
            : Promise.resolve({ data: [], error: null }),
          canViewBusinessFinance || profile.default_account_kind === "client"
            ? supabase
                .from("invoices")
                .select("*")
                .eq("organization_id", resolvedOrganizationId)
                .order("issued_at", { ascending: false })
            : Promise.resolve({ data: [], error: null }),
          supabase
            .from("app_notifications")
            .select("*")
            .eq("organization_id", resolvedOrganizationId)
            .order("created_at", { ascending: false }),
          supabase.rpc("get_workspace_access", {
            target_organization_id: resolvedOrganizationId,
          }),
          resolvedIsOwner
            ? supabase.rpc("list_client_connection_statuses")
            : Promise.resolve({
                data: resolvedClientId
                  ? [
                      {
                        client_id: resolvedClientId,
                        connection_state: "connected",
                        code_hint: null,
                        invitation_expires_at: null,
                      },
                    ]
                  : [],
                error: null,
              }),
          canViewCommunications
            ? supabase.rpc("list_conversation_presence")
            : Promise.resolve({ data: [], error: null }),
        ]);

        const firstError = [
          organizationResult,
          clientsResult,
          appointmentsResult,
          employeesResult,
          conversationsResult,
          messagesResult,
          attachmentsResult,
          invoicesResult,
          notificationsResult,
          subscriptionResult,
          connectionStatusesResult,
          presenceResult,
        ].find((result) => result.error)?.error;
        if (firstError) throw firstError;

        const organizationRows = (organizationResult.data ??
          []) as Organization[];
        if (!organizationRows[0])
          throw new Error("Workspace not found or access denied");
        setOrganization(organizationRows[0]);
        const loadedClients = (clientsResult.data ?? []) as ClientRecord[];
        setClients(loadedClients.filter((client) => !client.archived_at));
        setArchivedClients(
          loadedClients.filter((client) => Boolean(client.archived_at)),
        );
        setAppointments((appointmentsResult.data ?? []) as Appointment[]);
        setEmployees((employeesResult.data ?? []) as Employee[]);
        setConversations((conversationsResult.data ?? []) as Conversation[]);
        setMessages((messagesResult.data ?? []) as ChatMessage[]);
        setAttachments((attachmentsResult.data ?? []) as MessageAttachment[]);
        setInvoices((invoicesResult.data ?? []) as Invoice[]);
        setNotifications((notificationsResult.data ?? []) as AppNotification[]);
        const access = (subscriptionResult.data?.[0] ?? null) as null | {
          organization_id: string;
          plan_tier: WorkspaceSubscription["plan_tier"];
          subscription_status: WorkspaceSubscription["status"];
          access_source: WorkspaceSubscription["access_source"];
          trial_ends_at: string | null;
          current_period_ends_at: string | null;
          product_id: string | null;
          billing_cycle: WorkspaceSubscription["billing_cycle"];
          cancel_at_period_end: boolean;
          auto_renews: boolean;
          pending_plan_tier: WorkspaceSubscription["pending_plan_tier"];
          pending_billing_cycle: WorkspaceSubscription["pending_billing_cycle"];
          pending_change_effective_at: string | null;
        };
        setSubscription(
          access
            ? {
                organization_id: access.organization_id,
                plan_tier: access.plan_tier,
                status: access.subscription_status,
                access_source: access.access_source,
                trial_ends_at: access.trial_ends_at,
                current_period_ends_at: access.current_period_ends_at,
                product_id: access.product_id ?? null,
                billing_cycle: access.billing_cycle ?? null,
                cancel_at_period_end: access.cancel_at_period_end ?? false,
                auto_renews: access.auto_renews,
                pending_plan_tier: access.pending_plan_tier ?? null,
                pending_billing_cycle: access.pending_billing_cycle ?? null,
                pending_change_effective_at:
                  access.pending_change_effective_at ?? null,
              }
            : null,
        );
        const connectionRows = (connectionStatusesResult.data ??
          []) as ClientConnectionStatus[];
        setClientConnectionStatuses(
          Object.fromEntries(connectionRows.map((row) => [row.client_id, row])),
        );
        const presenceRows = (presenceResult.data ??
          []) as ConversationPresence[];
        setConversationPresence(
          Object.fromEntries(
            presenceRows.map((row) => [row.conversation_id, row]),
          ),
        );
      } catch (caught) {
        const message =
          caught instanceof Error
            ? t(caught.message)
            : t("Unable to load this workspace.");
        setError(message);
        if (!silent) toast.error(message);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [clear, profile, t, user],
  );

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!organizationId) return;
    const tables = isEmployeeAccount
      ? ["appointments", "clients", "employees", "app_notifications"]
      : [
          "appointments",
          "clients",
          "employees",
          "conversations",
          "messages",
          "message_attachments",
          "invoices",
          "app_notifications",
          "client_account_links",
        ];
    const channel = supabase.channel(`workspace:${organizationId}`);
    const queueRefresh = () => {
      if (realtimeRefreshTimer.current !== null) {
        window.clearTimeout(realtimeRefreshTimer.current);
      }
      realtimeRefreshTimer.current = window.setTimeout(() => {
        realtimeRefreshTimer.current = null;
        void refresh(true);
      }, 180);
    };
    for (const table of tables) {
      channel.on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table,
          filter: `organization_id=eq.${organizationId}`,
        },
        queueRefresh,
      );
    }
    if (!isEmployeeAccount) {
      channel.on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_presence" },
        queueRefresh,
      );
    }
    channel.subscribe();
    return () => {
      if (realtimeRefreshTimer.current !== null) {
        window.clearTimeout(realtimeRefreshTimer.current);
        realtimeRefreshTimer.current = null;
      }
      void supabase.removeChannel(channel);
    };
  }, [isEmployeeAccount, organizationId, refresh]);

  useEffect(() => {
    if (!organizationId || !isEmployeeAccount) return;
    const timer = window.setInterval(() => void refresh(true), 15_000);
    return () => window.clearInterval(timer);
  }, [isEmployeeAccount, organizationId, refresh]);

  useEffect(() => {
    if (!organizationId || !user || isEmployeeAccount) return;
    const updatePresence = (online: boolean) => {
      void supabase.rpc("set_my_presence", { p_online: online });
    };
    const reportVisibility = () =>
      updatePresence(document.visibilityState === "visible");
    const reportFocus = () => updatePresence(true);
    const reportBlur = () => updatePresence(false);
    reportVisibility();
    const heartbeat = window.setInterval(() => {
      if (document.visibilityState === "visible") updatePresence(true);
    }, 30_000);
    document.addEventListener("visibilitychange", reportVisibility);
    window.addEventListener("focus", reportFocus);
    window.addEventListener("blur", reportBlur);
    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", reportVisibility);
      window.removeEventListener("focus", reportFocus);
      window.removeEventListener("blur", reportBlur);
      updatePresence(false);
    };
  }, [isEmployeeAccount, organizationId, user]);

  const value = useMemo<WorkspaceValue>(
    () => ({
      loading,
      refreshing,
      error,
      accountKind,
      organizationId,
      activeClientId,
      organization,
      membership,
      isOwner: accountKind === "business" && membership?.role === "owner",
      isEmployee: accountKind === "business" && membership?.role !== "owner",
      currentEmployee:
        employees.find(
          (employee) => employee.profile_id === user?.id && employee.is_active,
        ) ?? null,
      subscription,
      clients,
      archivedClients,
      appointments,
      employees,
      conversations,
      messages,
      attachments,
      invoices,
      notifications,
      clientConnectionStatuses,
      conversationPresence,
      refresh,
      clientFor: (id) =>
        clients.find((client) => client.id === id) ??
        archivedClients.find((client) => client.id === id),
      conversationFor: (clientId) =>
        conversations.find(
          (conversation) => conversation.client_id === clientId,
        ),
      connectionStatusFor: (clientId) =>
        clientConnectionStatuses[clientId] ?? {
          client_id: clientId,
          connection_state: "not_connected",
          code_hint: null,
          invitation_expires_at: null,
        },
    }),
    [
      accountKind,
      activeClientId,
      appointments,
      archivedClients,
      attachments,
      clients,
      conversations,
      employees,
      error,
      invoices,
      loading,
      membership,
      messages,
      notifications,
      organization,
      organizationId,
      refresh,
      clientConnectionStatuses,
      conversationPresence,
      refreshing,
      subscription,
      user?.id,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const value = useContext(WorkspaceContext);
  if (!value)
    throw new Error("useWorkspace must be used inside WorkspaceProvider");
  return value;
}
